import QuillDelta from "quill-delta";
import Quill from "quill";
import "../quill.snow.css";
import WhiteboardControllerInterface from "../types/WhiteboardControllerInterface";
import { Scope } from "../types/Scope";
import ToolType from "../types/ToolType";
import makeQuillToolbar from "./makeQuillToolbar";
import canvasTxt from "canvas-txt";
import throttle from "lodash.throttle";
import KefirBus from "../KefirBus";

export interface QuillHelperShapeData {
  text: QuillDelta | null;
  maxWidth: number | null;
}

export interface QuillHelperDelegate {
  quillHelperWantsToSetParentElementClassName(className: string): void;
  quillHelperWantsToSetParentElementUserSelect(value: string): void;
  quillHelperWantsToSave(scope: Scope): void;
  quillHelperWantsToDelete(): void;
}

export class QuillHelper {
  quill: Quill;
  toolbarEl: HTMLDivElement;
  quillEl: HTMLDivElement;
  isEnabled = true;

  // quill hasFocus is too finicky
  fakeFocus = false;

  private sendTextContentsLive: () => void;

  get toolChangesBus(): KefirBus<ToolType, void> {
    return this.wb.toolChangesBus;
  }
  get focusedTextBus(): KefirBus<string | null, void> {
    return this.wb.drawing.focusedTextShapeIDBus;
  }

  constructor(
    private wb: WhiteboardControllerInterface,
    private id: string,
    private parentEl: HTMLElement,
    private shapeData: QuillHelperShapeData,
    private delegate: QuillHelperDelegate
  ) {
    this.sendTextContentsLive = throttle(this._sendTextContentsLive, 1000, {
      leading: true,
    });

    this.quillEl = document.createElement("div");

    parentEl.appendChild(this.quillEl);
    this.applyMaxWidth();

    this.toolbarEl = makeQuillToolbar();
    this.toolbarEl.style.display = "none";
    document
      .querySelector(".QuillToolbarContainer")
      ?.appendChild(this.toolbarEl);
    this.quill = new Quill(this.quillEl, {
      theme: "snow",
      placeholder: "",
      modules: {
        toolbar: {
          container: this.toolbarEl,
        },
      },
    });
    this.quill.on("text-change", this.onTextChange);
    this.quill.setContents(this.shapeData.text || new QuillDelta());
    this.quill.disable();

    this.delegate.quillHelperWantsToSetParentElementClassName(
      this.outerClassName
    );

    this.focusedTextBus.stream.onValue(this.onFocusedTextIDChanged);
  }

  teardown() {
    this.focusedTextBus.stream.offValue(this.onFocusedTextIDChanged);
    this.toolbarEl.parentElement?.removeChild(this.toolbarEl);
  }

  setIsEnabled(isEnabled: boolean) {
    this.isEnabled = isEnabled;
    if (isEnabled) {
      this.quillEl.style.display = "block";
    } else {
      this.quillEl.style.display = "none";
    }
  }

  onFocusedTextIDChanged = (id: string | null) => {
    if (id === this.id && this.isEnabled) {
      if (this.fakeFocus) return;
      this.focus();
    } else {
      if (!this.fakeFocus) return;
      this.onBlur();
    }
  };

  private reflectFocusStateAfterQuillIsFocused() {
    this.fakeFocus = true;
    this.delegate.quillHelperWantsToSetParentElementClassName(
      this.outerClassName
    );
    this.toolbarEl.style.display = "block";
    if (this.focusedTextBus.lastEmittedValue !== this.id) {
      this.focusedTextBus.emit(this.id);
    }
  }

  update(shapeData: QuillHelperShapeData, isLive: boolean) {
    this.shapeData = shapeData;
    if (shapeData.text && isLive) {
      if (this.fakeFocus) {
        // don't bother logging, this will happen all the time
        // console.error("Discarding simultaneous change:", this.shape.text);
      } else {
        this.quill.setContents(shapeData.text);
      }
    }
    // this.el.dataset.shape = JSON.stringify(newShape);
    this.delegate.quillHelperWantsToSetParentElementClassName(
      this.outerClassName
    );
  }

  get outerClassName(): string {
    const names: string[] = ["WBTextEditor"];
    if (this.fakeFocus) {
      // can't trust quill's hasFocus
      names.push("m-focus");
      // Prevent SimplePointerEventHandler from seeing events in here
      names.push("NoPointerEventsPlease");
    } else {
      names.push("m-blur");
    }
    if (this.shapeData.maxWidth) {
      names.push("m-fixed-width");
    } else {
      names.push("m-dynamic-width");
    }
    return names.join(" ");
  }

  // eslint-disable-next-line
  onTextChange = (
    delta: QuillDelta,
    oldContents: QuillDelta,
    source: string
  ) => {
    if (source !== "user") return;

    this.sendTextContentsLive();

    // This is 'optimal' but there's some issue with extra line breaks at the end.
    // this.wb.shapeStore.sendAppend(Scope.Live, this.id, {
    //   key: AttributeKey.Text,
    //   value: delta,
    // });
  };

  private focus() {
    if (this.fakeFocus) return;
    this.quill.enable();
    this.quill.focus();
    this.reflectFocusStateAfterQuillIsFocused();
  }

  save(scope = Scope.Persist) {
    this.delegate.quillHelperWantsToSave(scope);
  }

  private _sendTextContentsLive = () => {
    // Go ahead and save permanently. Performance might be meh but it would suck
    // to lose changes just because you reloaded without clicking out first.
    this.save(Scope.Persist);
  };

  onBlur() {
    this.fakeFocus = false;
    this.toolbarEl.style.display = "none";
    this.delegate.quillHelperWantsToSetParentElementClassName(
      this.outerClassName
    );

    if (this.quill.getText() === "\n" || this.quill.getText().length < 1) {
      // used to delete when empty but that's actually not good
      // this.delegate.quillHelperWantsToDelete();
    } else {
      this.save();
    }

    this.quill.disable();

    // cf. SelectTool.onClick
    this.wb.popToolType(ToolType.Text);
  }

  applyMaxWidth() {
    if (this.shapeData.maxWidth) {
      this.quillEl.style.width = `${this.shapeData.maxWidth}px`;
      this.quillEl.style.whiteSpace = "pre-wrap";
    } else {
      this.quillEl.style.width = "";
      this.quillEl.style.whiteSpace = "pre";
    }
  }

  // Canvas rendering

  renderShapeToCanvas(ctx: CanvasRenderingContext2D, scaleFactor: number) {
    ctx.scale(scaleFactor, scaleFactor);

    // const format = this.quill.getFormat(0, 1);
    const style = findLeafNodeStyle(this.quillEl.children[0].children[0]);

    ctx.font = style.getPropertyValue("font");
    ctx.fillStyle = style.getPropertyValue("color");
    ctx.textBaseline = "top";

    canvasTxt.vAlign = "top";
    canvasTxt.fontStyle = style.getPropertyValue("font-style");
    canvasTxt.fontVariant = style.getPropertyValue("font-variant");
    canvasTxt.fontWeight = style.getPropertyValue("font-weight");
    canvasTxt.font = style.getPropertyValue("font-family");
    // e.g. "25.56px". Fortunately we use pixel sizes.
    canvasTxt.lineHeight = parseFloat(style.getPropertyValue("line-height"));
    canvasTxt.fontSize = parseFloat(style.getPropertyValue("font-size"));

    switch (style.getPropertyValue("text-align")) {
      case "left":
        ctx.textAlign = "left";
        canvasTxt.align = "left";
        break;
      case "right":
        ctx.textAlign = "right";
        canvasTxt.align = "right";
        break;
      case "center":
        ctx.textAlign = "center";
        canvasTxt.align = "center";
        break;
      case "start":
        ctx.textAlign = "start";
        canvasTxt.align = "left";
        break;
      case "end":
        ctx.textAlign = "end";
        canvasTxt.align = "right";
        break;
      default:
        ctx.textAlign = "start";
        canvasTxt.align = "left";
        break;
    }

    // Tons of magic numbers here, just go with it
    let maxWidth = this.shapeData.maxWidth;
    if (maxWidth) {
      maxWidth -= 12 * scaleFactor; // magic number!!!
    } else {
      maxWidth = (this.parentEl.clientWidth + 100) * scaleFactor; // just make it big
    }
    canvasTxt.drawText(
      ctx,
      this.quill.getText(),
      3,
      -10,
      maxWidth,
      (this.parentEl.clientHeight + 100) * scaleFactor
    );
  }
}

function findLeafNodeStyle(ancestor: Element): CSSStyleDeclaration {
  let nextAncestor = ancestor;
  while (nextAncestor.children.length) {
    nextAncestor = nextAncestor.children[0];
  }
  return window.getComputedStyle(nextAncestor, null);
}
